import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './betsTable.module.scss';
import cx from 'classnames';
import Dropdown from 'components/dropdown/dropdown';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import { openBetDetailsModal } from 'store/uiSlice';
import { useGetAllBetsQuery, useGetMyBetsQuery, useGetWinnersQuery } from 'store/gamesApi';
import { skipToken } from '@reduxjs/toolkit/query/react';
import Skeleton from 'components/skeleton/Skeleton';

const BetsTable = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('all');
  const [limit, setLimit] = useState('5');
  const [tick, setTick] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const { isBetDetailsOpen } = useAppSelector((state) => state.ui);
  const tabsRef = useRef<HTMLDivElement>(null);

  const handleRowClick = (item: any) => {
    const roundId =
      item.roundIdCasino ||
      item.roundId ||
      item.mdId ||
      item.betId ||
      item.id ||
      'Cf2zfEPUpAoU8q3UMB2cr';

    dispatch(
      openBetDetailsModal({
        roundId,
        initialData: {
          betId: roundId,
          gameId: item.gameId || item.game?.id || item.id,
          gameTitle: item.gameTitle || item.gameName,
          gameIcon: item.imageUrl || item.imgUrl,
          gameSlug: item.gameSlug || item.slug,
          userName: item.username || item.userId,
          userVipLevel: item.userVipLevel,
          userVipIcon: item.userVipIcon,
          betAmount: item.betAmount,
          multiplier: item.multiplier,
          payoutAmount: item.payoutAmount ?? item.amount ?? item.winAmount,
          date: item.createdAt || item.date || item.timestamp,
          replyUrl: item.replyUrl || item.gameReplyUrl,
          gameReplyUrl: item.gameReplyUrl || item.replyUrl,
        },
      }),
    );
  };

  const handleTabClick = (tab: string, e: React.MouseEvent<HTMLButtonElement>) => {
    setActiveTab(tab);
    if (tabsRef.current) {
      const target = e.currentTarget;
      const targetLeft = target.offsetLeft;
      const targetWidth = target.offsetWidth;
      const containerWidth = tabsRef.current.offsetWidth;
      const scrollTarget = targetLeft - containerWidth / 2 + targetWidth / 2;
      tabsRef.current.scrollTo({
        left: Math.max(0, scrollTarget),
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    if (isHovered || isBetDetailsOpen) return;

    const interval = setInterval(() => {
      setTick((t) => t + 1);
    }, 1200);
    return () => clearInterval(interval);
  }, [isHovered, isBetDetailsOpen]);

  const limitOptions = [
    { value: '5', label: '5' },
    { value: '10', label: '10' },
    { value: '20', label: '20' },
    { value: '30', label: '30' },
  ];

  const limitNum = parseInt(limit, 10);

  const { data: allBetsRes, isLoading: isAllBetsLoading } = useGetAllBetsQuery(
    { page: 1, limit: 30 },
    {
      skip: activeTab !== 'all',
      pollingInterval: 5000,
      skipPollingIfUnfocused: true,
    },
  );

  const { data: myBetsRes, isLoading: isMyBetsLoading } = useGetMyBetsQuery(
    { page: 1, limit: limitNum },
    {
      skip: activeTab !== 'my' || !isAuthenticated,
      pollingInterval: 5000,
      skipPollingIfUnfocused: true,
    },
  );

  const isWinnersTab = activeTab === 'top' || activeTab === 'last';

  const { data: response, isLoading: isWinnersLoading } = useGetWinnersQuery(
    isWinnersTab ? undefined : skipToken,
    {
      skip: !isWinnersTab,
      pollingInterval: isWinnersTab ? 20000 : undefined,
      skipPollingIfUnfocused: true,
    },
  );

  const extractArray = (data: any): any[] => {
    if (Array.isArray(data)) return data;
    if (!data || typeof data !== 'object') return [];
    if (Array.isArray(data.items)) return data.items;
    if (Array.isArray(data.data)) return data.data;
    if (Array.isArray(data.rows)) return data.rows;
    if (Array.isArray(data.winners)) return data.winners;
    const firstArray = Object.values(data).find((val) => Array.isArray(val));
    if (Array.isArray(firstArray)) return firstArray;
    return [];
  };

  const allBetsRaw = useMemo(
    () => extractArray(allBetsRes?.data?.items || allBetsRes?.data),
    [allBetsRes],
  );
  const myBetsRaw = useMemo(
    () => extractArray(myBetsRes?.data?.items || myBetsRes?.data),
    [myBetsRes],
  );
  const topWinnersRaw = useMemo(
    () => extractArray(response?.data?.topWinners ?? response?.topWinners),
    [response],
  );
  const lastWinnersRaw = useMemo(
    () => extractArray(response?.data?.lastWinners ?? response?.lastWinners),
    [response],
  );

  const activeData = useMemo(() => {
    let list: any[] = [];
    switch (activeTab) {
      case 'my':
        list = myBetsRaw;
        break;
      case 'top':
        list = topWinnersRaw;
        break;
      case 'last':
        list = lastWinnersRaw;
        break;
      case 'all':
      default:
        list = allBetsRaw;
        break;
    }
    return Array.isArray(list) ? list.slice(0, limitNum) : [];
  }, [activeTab, allBetsRaw, myBetsRaw, topWinnersRaw, lastWinnersRaw, limitNum]);

  const isLoading =
    activeTab === 'all'
      ? isAllBetsLoading
      : activeTab === 'my'
        ? isMyBetsLoading
        : isWinnersLoading;

  const rotatedData = useMemo(() => {
    if (!Array.isArray(activeData) || activeData.length <= 1) return activeData;
    // Rotate for "All bets", "Top winners", and "Last winners". Keep "My bets" static.
    if (activeTab === 'my') return activeData;

    const shift = tick % activeData.length;
    if (shift === 0) return activeData;

    const bottom = activeData.slice(-shift);
    const top = activeData.slice(0, activeData.length - shift);
    return [...bottom, ...top];
  }, [activeData, tick, activeTab]);

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.nav}>
          <div className={styles.title}>
            <span className={styles.clockIcon}>🕒</span> {t('betsTable.title', 'Bets')}
          </div>
          <div ref={tabsRef} className={styles.tabs}>
            {isAuthenticated && (
              <button
                className={cx({ [styles.active]: activeTab === 'my' })}
                onClick={(e) => handleTabClick('my', e)}
              >
                {t('betsTable.tabs.myBets', 'My bets')}
              </button>
            )}
            <button
              className={cx({ [styles.active]: activeTab === 'all' })}
              onClick={(e) => handleTabClick('all', e)}
            >
              {t('betsTable.tabs.allBets', 'All bets')}
            </button>
            <button
              className={cx({ [styles.active]: activeTab === 'top' })}
              onClick={(e) => handleTabClick('top', e)}
            >
              {t('betsTable.tabs.topWinners', 'Top winners')}
            </button>
            <button
              className={cx({ [styles.active]: activeTab === 'last' })}
              onClick={(e) => handleTabClick('last', e)}
            >
              {t('betsTable.tabs.lastWinners', 'Last winners')}
            </button>
          </div>
        </div>

        <Dropdown
          options={limitOptions}
          value={limit}
          onChange={setLimit}
          width={80}
          className={styles.dropdownBtn}
        />
      </header>

      <div className={styles.responsiveScroll}>
        <div className={cx(styles.tableMinWidth, { [styles.myBets]: activeTab === 'my' })}>
          <div className={styles.thead}>
            <div className={styles.tr}>
              {activeTab !== 'my' && <span>{t('betsTable.columns.user', 'User')}</span>}
              <span>{t('betsTable.columns.game', 'Game')}</span>
              <span className={styles.betAmountCol}>{t('betsTable.columns.betAmount', 'Bet amount')}</span>
              <span>{t('betsTable.columns.multiplier', 'Multiplier')}</span>
              <span>{t('betsTable.columns.payout', 'Payout')}</span>
            </div>
          </div>

          <div className={styles.viewport}>
            <div
              key={activeTab === 'my' ? activeTab : `${activeTab}-${tick}`}
              className={styles.tbody}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              {isLoading ? (
                Array.from({ length: limitNum }).map((_, idx) => (
                  <div key={`skel-${idx}`} className={styles.trBody} style={{ cursor: 'default' }}>
                    {activeTab !== 'my' && (
                      <div className={styles.td}>
                        <Skeleton style={{ width: '60px', height: '14px', borderRadius: '4px' }} />
                      </div>
                    )}
                    <div className={styles.td}>
                      <Skeleton style={{ width: '20px', height: '20px', borderRadius: '4px' }} />{' '}
                      <Skeleton
                        style={{
                          width: '80px',
                          height: '14px',
                          borderRadius: '4px',
                          marginLeft: '8px',
                        }}
                      />
                    </div>
                    <div className={cx(styles.td, styles.betAmountCol)}>
                      <Skeleton style={{ width: '50px', height: '14px', borderRadius: '4px' }} />
                    </div>
                    <div className={styles.td}>
                      <Skeleton style={{ width: '40px', height: '14px', borderRadius: '4px' }} />
                    </div>
                    <div className={styles.td}>
                      <Skeleton style={{ width: '50px', height: '14px', borderRadius: '4px' }} />
                    </div>
                  </div>
                ))
              ) : !Array.isArray(rotatedData) || rotatedData.length === 0 ? (
                <div className={styles.emptyState}>
                  {activeTab === 'top' || activeTab === 'last'
                    ? t('betsTable.emptyWinners', 'No winners found')
                    : t('betsTable.emptyHistory', 'No bet history found')}
                </div>
              ) : (
                rotatedData.map((item: any, idx: number) => {
                  if (!item) return null;
                  const rawPayout = item.payoutAmount ?? item.amount ?? item.winAmount ?? '0';
                  const payoutNum = parseFloat(String(rawPayout || '0'));
                  const isWin = payoutNum > 0;
                  const betAmountNum =
                    item.betAmount != null ? parseFloat(String(item.betAmount)) : null;

                  let multiplierStr = item.multiplier != null ? String(item.multiplier) : '';
                  if (multiplierStr && !multiplierStr.endsWith('x')) {
                    multiplierStr = `${multiplierStr}x`;
                  } else if (!multiplierStr && betAmountNum && betAmountNum > 0 && payoutNum > 0) {
                    multiplierStr = `${(payoutNum / betAmountNum).toFixed(2)}x`;
                  } else if (!multiplierStr) {
                    multiplierStr = '-';
                  }

                  return (
                    <div
                      key={`${activeTab}-${idx}-${item.username || item.userId || ''}-${item.gameTitle || ''}`}
                      className={styles.trBody}
                      onClick={() => handleRowClick(item)}
                      onMouseEnter={() => setIsHovered(true)}
                    >
                      {activeTab !== 'my' && (
                        <div className={styles.td}>
                          <span className={styles.ellipsisText}>
                            {item.username || item.userId || t('betsTable.anonymous', 'Anonymous')}
                          </span>
                        </div>
                      )}
                      <div className={styles.td}>
                        <img src={item.imageUrl || ''} alt="" className={styles.gameThumb} />{' '}
                        <span className={styles.gameName}>
                          {item.gameTitle || t('betsTable.unknownGame', 'Unknown Game')}
                        </span>
                      </div>
                      <div className={cx(styles.td, styles.betAmountCol)}>
                        {betAmountNum !== null && !isNaN(betAmountNum)
                          ? new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: 'USD',
                            }).format(betAmountNum)
                          : '-'}
                      </div>
                      <div className={styles.td}>{multiplierStr}</div>
                      <div
                        className={cx(styles.td, styles.payoutCol, {
                          [styles.win]: isWin,
                          [styles.loss]: !isWin,
                        })}
                      >
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                        }).format(isNaN(payoutNum) ? 0 : payoutNum)}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BetsTable;
